/*
 * Product media gallery thumbnail hover.
 *
 * Moving the pointer onto a thumbnail brings that photo up in the main viewer,
 * so the gallery can be browsed without clicking, and hovering either end of
 * the strip pages it to the next or previous set.
 *
 * The photo change is handed to the theme's own <media-gallery> element,
 * exactly as a click on the thumbnail would be, so the highlight, the
 * accessibility live region and video/3D playback all stay in sync.
 *
 * Mouse only: on a touchscreen the same gesture is a tap, which the theme
 * already handles as a click.
 */
if (!window.galleryHoverNavigation) {
  window.galleryHoverNavigation = true;

  // Long enough that a pointer crossing the strip on its way somewhere else
  // doesn't pull the viewer through every photo it passes over.
  const HOVER_DELAY = 80;

  let timer = 0;
  let lastX = null;
  let lastY = null;

  const cancel = () => {
    clearTimeout(timer);
    timer = 0;
  };

  const getStrip = (gallery) =>
    gallery.querySelector('[id^="GalleryThumbnails"] .thumbnail-list');

  // The viewer's slides are lazy, so start the fetch as soon as the pointer
  // arrives rather than when the delay runs out.
  const warm = (gallery, mediaId) => {
    const slide = gallery.querySelector(
      `[id^="GalleryViewer"] .product__media-item[data-media-id="${mediaId}"]`
    );
    if (!slide) return;
    slide.querySelectorAll('img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });
  };

  // Where the strip should end up when a thumbnail at one of its ends is
  // hovered, so the whole gallery can be walked without ever clicking.
  //
  // This has to be worked out before the photo changes. The theme moves the
  // strip itself on the way through -- it re-scrolls a newly active thumbnail
  // that its own visibility test counts as off-screen, which lands the hovered
  // one at the left edge. Measured after that, every forward page looks like a
  // thumbnail sitting at the start, and the answer comes back "page backwards",
  // undoing the move that just happened.
  const planPaging = (strip, thumbnail) => {
    if (!strip) return null;

    const left = thumbnail.offsetLeft;
    const right = left + thumbnail.clientWidth;
    const viewLeft = strip.scrollLeft;
    const viewRight = viewLeft + strip.clientWidth;
    // How close to an edge counts as being the one at that edge.
    const edge = thumbnail.clientWidth / 2;

    // First one showing: pull the previous set in behind it.
    if (viewLeft > 0 && left - viewLeft <= edge) {
      return Math.max(right - strip.clientWidth, 0);
    }

    // Last one showing: pull the next set in ahead of it.
    if (viewRight < strip.scrollWidth && viewRight - right <= edge) {
      return left;
    }

    return null;
  };

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const thumbnail = event.target.closest('[id^="GalleryThumbnails"] [data-target]');
    if (!thumbnail) {
      // The pointer has moved off the strip before the delay ran out.
      cancel();
      lastX = null;
      lastY = null;
      return;
    }

    // Paging the strip slides a different thumbnail under a pointer that never
    // moved, and the browser reports that as a fresh hover. Left alone it walks
    // the gallery along on its own.
    const stationary = event.clientX === lastX && event.clientY === lastY;
    lastX = event.clientX;
    lastY = event.clientY;
    if (stationary) return;

    const gallery = thumbnail.closest('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    const mediaId = thumbnail.dataset.target;
    const showing = gallery.querySelector(
      '[id^="GalleryViewer"] .product__media-item.is-active'
    );
    const alreadyShowing = showing && showing.dataset.mediaId === mediaId;

    warm(gallery, mediaId);
    cancel();

    timer = setTimeout(() => {
      // A drag in progress owns the viewer.
      if (gallery.querySelector('.gallery-dragging')) return;

      const strip = getStrip(gallery);
      const pageTo = planPaging(strip, thumbnail);

      if (!alreadyShowing) {
        // Stop a slide still travelling from a drag, so it doesn't finish on
        // top of the photo being hovered.
        document.dispatchEvent(new CustomEvent('gallery:interrupt'));
        gallery.setActiveMedia(mediaId, false);
      }

      // Last, so it settles the strip wherever the theme left it.
      if (pageTo !== null) strip.scrollTo({ left: pageTo });
    }, HOVER_DELAY);
  });
}
